import { useState } from 'react';
import type { FormEvent } from 'react';
import { ApiRequestError } from '../api/apiRequestError';
import { postCreateTest } from '../api/postCreateTest';
import { privateVideoGuidance } from '../../shared/privateVideoGuidance';
import { CopyableLinkField } from '../copyableLinkField';

type CreatedTest = { share_url: string; dashboard_url: string; owned: boolean };

// The creator's entire onboarding: paste a URL, get two links.
//
// No signup, no account, no email (DESIGN-DECISIONS §7). The cost of that is
// that the dashboard link is the only way back to the results, forever, and this
// screen is the one place we can say so. Hence the unmissable warning — it is a
// product requirement (spec §5), not decoration.
export function CreateTestScreen() {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  // LIVE by default (Cowork023 §1), reversing the Cowork017 §1 default. This is a
  // UI preference only. The DATABASE defaults in migrations 0004 and 0005 stay
  // 'blind' and must not follow it: a column default is a fail-closed floor for a
  // row that arrives without a mode, and for such a row 'blind' is still the
  // honest answer — claiming 'live' would assert that someone saw other people's
  // reactions when they may not have. This form always sends a mode explicitly.
  const [mode, setMode] = useState<'blind' | 'live'>('live');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedTest | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await postCreateTest(youtubeUrl, mode);
      const origin = window.location.origin;
      setCreated({
        owned: result.owned,
        // The server sends absolute URLs when PUBLIC_BASE_URL is configured. If
        // it is not, fall back to this origin rather than handing the creator a
        // relative path they cannot paste into a group chat.
        share_url: result.share_url.startsWith('http') ? result.share_url : `${origin}/t/${result.test_id}`,
        dashboard_url: result.dashboard_url.startsWith('http')
          ? result.dashboard_url
          : `${origin}/d/${result.dashboard_key}`
      });
    } catch (caught) {
      // The private-video message from spec §7 is authored server-side and shown
      // verbatim. It is the one error in v0 that tells the creator exactly what
      // to change and where, and rewording it here would waste that.
      setError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <main className="pageWrap">
        <h1>
          what<span>landed</span>
        </h1>
        <p className="pageSub">Your test is ready. Two links, and they do different jobs.</p>

        <CopyableLinkField
          label="Share this with your reviewers"
          url={created.share_url}
          hint="Anyone with this link can watch and react. They never see each other's reactions."
        />

        {/* Cowork020 §1. The warning below is true and important for an
            ANONYMOUS creator, and false in every clause for a signed-in one —
            their test is in /my permanently. Cowork named only the no-JS
            renderer, but this screen carried the identical copy and the identical
            falsehood, so it branches the same way. */}
        <CopyableLinkField
          emphasis={!created.owned}
          label={created.owned ? 'Your dashboard' : 'Your dashboard — save it now'}
          url={created.dashboard_url}
          hint={
            created.owned
              ? 'Where your results appear. You can always reach it from My tests, so there is nothing you need to save.'
              : 'This is the only way back to your results. There is no login and no email recovery. If you lose this link, the test is gone.'
          }
        />

        {created.owned ? (
          <div className="callout">
            <b>Saved to your tests.</b> This test belongs to your account and will be kept
            until you delete it. You do not need to hold on to the link.
          </div>
        ) : (
          <div className="calloutDanger">
            <b>Save the dashboard link before you close this page.</b> Put it somewhere you
            will find it again — a note, a bookmark, a message to yourself. We cannot send
            it to you later.
          </div>
        )}

        <div className="createResultActions">
          {created.owned ? (
            <a className="buttonPrimary buttonLink" href="/my">
              My tests
            </a>
          ) : null}
          <button className="buttonSecondary" type="button" onClick={() => { setCreated(null); setYoutubeUrl(''); }}>
            Create another test
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="pageWrap">
      <h1>
        what<span>landed</span>
      </h1>
      <p className="pageSub">
        Paste your cut, send the link round, and see which moments landed.
      </p>

      <form onSubmit={(event) => void submit(event)}>
        {/* The no-JS form on / has always had a label; this one relied on the
            placeholder, which disappears the moment anything is typed and is not
            announced as a label by a screen reader. Added here so both create
            paths say the same thing (Cowork023 §2). */}
        <label className="pageSub" htmlFor="youtube_url">
          Your YouTube link
        </label>
        <input
          type="text"
          id="youtube_url"
          inputMode="url"
          autoComplete="off"
          placeholder="https://www.youtube.com/watch?v=..."
          value={youtubeUrl}
          onChange={(event) => setYoutubeUrl(event.target.value)}
        />
        <fieldset className="modeChoice">
          <legend>How should reviewers watch?</legend>
          <label>
            {/* `value` is not read by anything — React state is what gets sent —
                but without it the DOM reports value="on", which is what any
                inspection of this page sees, and it makes the two create paths
                describe themselves differently. */}
            <input
              type="radio"
              name="mode"
              value="blind"
              checked={mode === 'blind'}
              onChange={() => setMode('blind')}
            />
            <span>
              <b>On their own</b> — they see nothing until they finish. Independent
              reactions, best if you plan to re-edit from the answer.
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="mode"
              value="live"
              checked={mode === 'live'}
              onChange={() => setMode('live')}
            />
            <span>
              <b>Alongside everyone else</b> — previous reactions float over the video
              as they watch. More fun, and closer to watching in a room.
            </span>
          </label>
        </fieldset>

        <button className="buttonPrimary" type="submit" disabled={busy || youtubeUrl.trim().length === 0}>
          {busy ? 'Checking the video…' : 'Create test'}
        </button>
      </form>

      {error ? <div className="calloutError">{error}</div> : null}

      {/* Same words as the server-side landing page and the §7 Private-video
          error, from one shared constant so they cannot drift (Cowork009 §5). */}
      <div className="callout">
        <b>{privateVideoGuidance.notice}</b>
      </div>
    </main>
  );
}
